import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';

interface Option {
    value: string;
    label: string;
}

interface SearchableSelectProps {
    options: Option[];
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    className?: string;
    disabled?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'Select an option',
    searchPlaceholder = 'Search...',
    className = '',
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [openUpward, setOpenUpward] = useState(false);
    const [openKey, setOpenKey] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const selectedItemRef = useRef<HTMLButtonElement>(null);

    const selectedOption = useMemo(
        () => options.find((opt) => opt.value === value),
        [options, value]
    );

    const filteredOptions = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();
        if (!query) return options;
        return options.filter((opt) =>
            opt.label.toLowerCase().includes(query) ||
            opt.value.toLowerCase().includes(query)
        );
    }, [options, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
        if (!isOpen) {
            setSearchQuery('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const dropdownHeight = 280;
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            setOpenUpward(spaceBelow < dropdownHeight && spaceAbove > spaceBelow);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && selectedItemRef.current && listRef.current) {
            requestAnimationFrame(() => {
                const list = listRef.current;
                const item = selectedItemRef.current;
                if (list && item) {
                    const listRect = list.getBoundingClientRect();
                    const itemRect = item.getBoundingClientRect();
                    const offsetTop = item.offsetTop - list.offsetTop;
                    list.scrollTop = offsetTop - (listRect.height / 2) + (itemRect.height / 2);
                }
            });
        }
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div className={`relative w-full ${className}`} ref={containerRef}>
            <button
                type="button"
                disabled={disabled}
                onClick={() => {
                    if (!isOpen) setOpenKey(k => k + 1);
                    setIsOpen(!isOpen);
                }}
                className={`flex h-10 w-full items-center justify-between rounded-lg border border-slate-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 ${isOpen ? 'ring-2 ring-indigo-500' : ''
                    }`}
            >
                <span className={`block truncate ${!selectedOption ? 'text-slate-400 dark:text-zinc-500' : ''}`}>
                    {selectedOption ? selectedOption.label : placeholder}
                </span>
                <ChevronDown
                    className={`h-4 w-4 text-slate-400 dark:text-zinc-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
                        }`}
                />
            </button>

            {isOpen && (
                <div
                    key={openKey}
                    className={`absolute z-50 max-h-72 w-full flex flex-col rounded-lg border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg animate-in fade-in zoom-in-95 duration-150 ${openUpward ? 'bottom-full mb-1 slide-in-from-bottom-2' : 'top-full mt-1 slide-in-from-top-2'}`}
                >
                    <div className="shrink-0 border-b border-slate-100 dark:border-zinc-800 bg-slate-50 dark:bg-zinc-800 p-2">
                        <div className="relative">
                            <Search className="absolute start-2.5 top-2.5 h-4 w-4 text-slate-400 dark:text-zinc-500" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full rounded-md border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 py-2 ps-8 pe-3 text-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-zinc-500 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute end-2 top-2.5 text-slate-400 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto p-1">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <button
                                    key={option.value}
                                    ref={value === option.value ? selectedItemRef : null}
                                    type="button"
                                    onClick={() => handleSelect(option.value)}
                                    className={`flex w-full items-center justify-between rounded-md px-3 py-2 text-start text-sm transition-colors hover:bg-slate-50 dark:hover:bg-zinc-800 ${value === option.value ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 font-medium' : 'text-slate-700 dark:text-zinc-300'
                                        }`}
                                >
                                    <span className="truncate">{option.label}</span>
                                    {value === option.value && <Check className="h-4 w-4 shrink-0" />}
                                </button>
                            ))
                        ) : (
                            <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-zinc-500">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
